import { FC, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { DragIndicator as DragIcon } from '@mui/icons-material';
import type { CatalogItem } from '../../types';
import { CatalogItemCard } from './catalog-item-card';

// ===== SORTABLE ITEM WRAPPER =====

interface SortableItemWrapperProps {
  id: string;
  children: React.ReactNode;
}

/**
 * Wraps a catalog card with sortable drag-and-drop behavior.
 * Shows a drag handle on hover.
 * @param root0 - The component props.
 * @param root0.id - The unique ID of the sortable item.
 * @param root0.children - The content to render inside the wrapper.
 * @returns The sortable wrapper element.
 */
const SortableItemWrapper: FC<SortableItemWrapperProps> = ({
  id,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        position: 'relative',
        '&:hover .drag-handle': {
          opacity: 1,
        },
      }}
    >
      {/* Drag handle shown on hover */}
      <IconButton
        className="drag-handle"
        size="small"
        sx={{
          position: 'absolute',
          top: 4,
          left: 4,
          zIndex: 1,
          opacity: 0,
          transition: 'opacity 0.2s',
          cursor: 'grab',
          bgcolor: 'background.paper',
          boxShadow: 1,
          '&:active': { cursor: 'grabbing' },
        }}
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <DragIcon fontSize="small" />
      </IconButton>
      {children}
    </Box>
  );
};

// ===== DRAG OVERLAY GHOST =====

interface DragGhostProps {
  item: CatalogItem;
}

const DragGhost: FC<DragGhostProps> = ({ item }) => (
  <Box
    sx={{
      opacity: 0.85,
      boxShadow: 6,
      cursor: 'grabbing',
      minWidth: 160,
    }}
  >
    <CatalogItemCard
      item={item}
      onEdit={() => null}
      onDelete={() => null}
      showPrimalBadge
    />
  </Box>
);

// ===== SORTABLE CATALOG GRID =====

export interface SortableCatalogGridProps {
  /** Items to display in the grid */
  items: CatalogItem[];
  /** Called when the user finishes a drag — receives ordered array of IDs */
  onReorder: (orderedIds: string[]) => Promise<void>;
  /** Render function for each item */
  renderItem: (item: CatalogItem) => React.ReactNode;
  /** Message to show when the list is empty */
  emptyMessage?: string;
}

/**
 * A drag-and-drop sortable grid for catalog items.
 * Wraps items from the catalog (cuts, trim, extras, specs) in a sortable grid
 * using @dnd-kit.  Drag handles appear on hover; reorder is persisted via the
 * `onReorder` callback after each drag.
 * @param root0 - The component props.
 * @param root0.items - Items to display in the grid.
 * @param root0.onReorder - Called with ordered IDs after each drag.
 * @param root0.renderItem - Render function for each item.
 * @param root0.emptyMessage - Message shown when the list is empty.
 * @returns The sortable grid element.
 * @example
 * <SortableCatalogGrid
 *   items={cuts}
 *   onReorder={reorderCuts}
 *   renderItem={(item) => (
 *     <CatalogItemCard item={item} onEdit={handleEdit} onDelete={handleDelete} />
 *   )}
 * />
 */
export const SortableCatalogGrid: FC<SortableCatalogGridProps> = ({
  items,
  onReorder,
  renderItem,
  emptyMessage = 'No items yet.',
}) => {
  // Local optimistic order — updated immediately on drag end, then persisted
  const [orderedIds, setOrderedIds] = useState<string[]>(() =>
    items.map((i) => i.id),
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  // Sync when server data changes (e.g., after refetch)
  const serverIds = useMemo(() => items.map((i) => i.id), [items]);
  // Only reset if the set of IDs has fundamentally changed (add/remove), not
  // just on every re-render.
  const prevServerRef = useMemo(
    () => ({ ids: serverIds }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverIds.join(',')],
  );
  if (
    prevServerRef.ids !== orderedIds &&
    serverIds.join(',') !== orderedIds.join(',')
  ) {
    // If an item was added/removed, reset to server order
    const hasNewItems = serverIds.some((id) => !orderedIds.includes(id));
    const hasRemovedItems = orderedIds.some((id) => !serverIds.includes(id));
    if (hasNewItems || hasRemovedItems) {
      setOrderedIds(serverIds);
    }
  }

  const sortedItems = useMemo(
    () =>
      orderedIds
        .map((id) => items.find((i) => i.id === id))
        .filter(Boolean) as CatalogItem[],
    [orderedIds, items],
  );

  const activeItem = activeId ? items.find((i) => i.id === activeId) : null;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // Require 5px of movement before activating drag to avoid false clicks
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id as string);
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = orderedIds.indexOf(active.id as string);
    const newIndex = orderedIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(orderedIds, oldIndex, newIndex);
    // Optimistically update local state
    setOrderedIds(newOrder);
    // Persist to backend
    await onReorder(newOrder);
  };

  if (items.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={orderedIds}
        strategy={verticalListSortingStrategy}
      >
        <Stack spacing={2}>
          {sortedItems.map((item) => (
            <SortableItemWrapper key={item.id} id={item.id}>
              {renderItem(item)}
            </SortableItemWrapper>
          ))}
        </Stack>
      </SortableContext>

      {/* Dragging ghost overlay */}
      <DragOverlay>
        {activeItem ? <DragGhost item={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
};
